import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { ContentSaveIcon } from '@atgfr8/icons';
import { Button, Tab, TabsComponent, ToastType } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import {
  prepareProspectDetailsChangeSummary,
  setProspectDetailsChangeBaseline,
} from '@changeTracking/pages/prospectDetails/formChangeTracking';
import { SiteTitle } from '@components/SiteTitle/SiteTitle';
import { useNavigate, useParams } from '@solidjs/router';
import {
  getProspectDetails,
  ProspectData,
  updateProspectDetails,
  updateProspectDetailsApi,
} from '@store/prospect';
import {
  prospectDetailsStore,
  setProspectDetailsStore,
} from '@store/prospect/store';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeNewCustomerDetails } from '@utils/routeCustomerDetails';
import { handleToast } from '@utils/utils';
import { Quotes } from '@views/customer/customerDetail/quotes';
import {
  Component,
  createEffect,
  createSignal,
  For,
  Match,
  onCleanup,
  Show,
  Switch,
} from 'solid-js';
import { ValidationError } from 'yup';

import { ProspectDetailsInformation } from './components';
import { prospectDetailsSchema } from './validations';

const ProspectDetails: Component = () => {
  const [saveLoading, setLoading] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal(0);

  const navigate = useNavigate();
  const params = useParams();

  const tabs = ['Details', 'Quotes'];

  const statuses = [
    { id: 1, name: 'Lead', value: 'Lead', classes: 'bg-[#F0F8FF]! ' },
    {
      id: 2,
      name: 'Contacted',
      value: 'Contacted',
      classes: 'bg-[#FFFFFF]! ',
    },
    { id: 3, name: 'Quoting', value: 'Quoting', classes: 'bg-[#FFFFC1]! ' },
    {
      id: 4,
      name: 'Submitted',
      value: 'Submitted',
      classes: 'bg-[#9ACC9A]!   ',
    },
    { id: 5, name: 'Dead Lead', value: 'Dead Lead', classes: 'bg-[#CFD8DC]! ' },
    { id: 6, name: 'Approved', value: 'Approved', classes: ' ' },
  ];

  const handleProspectDetailsValidation = () => {
    try {
      setProspectDetailsStore('prospectDetailsErrors', null);
      prospectDetailsSchema.validateSync(prospectDetailsStore.prospectDetails, {
        abortEarly: false,
      });
      return true;
    } catch (validationError: unknown) {
      const errors: Record<string, string[]> = {};
      if (validationError instanceof ValidationError) {
        validationError.inner.forEach((error: ValidationError) => {
          if (error.path !== undefined) {
            errors[error.path] = error.errors;
          }
        });
        setProspectDetailsStore('prospectDetailsErrors', errors);
        return false;
      }
    }
  };

  const handleProspectDetailsSave = async () => {
    setLoading(true);
    if (handleProspectDetailsValidation() ?? false) {
      if (Boolean(prospectDetailsStore.prospectDetails.id)) {
        const updatedContact =
          prospectDetailsStore.prospectDetails.contacts.map((contact) => {
            return {
              ...contact,
              id: contact.operationType === 'Insert' ? 0 : contact.id,
            };
          });
        const payload: ProspectData = {
          ...prospectDetailsStore.prospectDetails,
          contacts: updatedContact,
          operationType: 'Update',
        };
        const changeSummary = prepareProspectDetailsChangeSummary(payload);

        const res = await updateProspectDetailsApi(
          payload,
          changeSummary.changedForms,
        );
        if (res && res.isSuccess) {
          handleToast(
            ToastType.Success,
            'Prospect details updated successfully',
          );
          setProspectDetailsStore('prospectDetails', res.value);
          setProspectDetailsChangeBaseline(res.value);
        }
      }
    }
    setLoading(false);
  };

  const handleSubmitForCredit = () => {
    const isNative = routeNewCustomerDetails(
      prospectDetailsStore.prospectDetails.id,
    );
    Boolean(isNative) &&
      navigate(`/customer/create/${prospectDetailsStore.prospectDetails.id}`);
  };

  createEffect(async () => {
    if (params.id) {
      await getProspectDetails(Number(params.id));
    }
    updateTabPanelHeight();
    window.addEventListener('resize', updateTabPanelHeight);
  });

  const updateTabPanelHeight = () => {
    const header = document.getElementById('prospect-details-header');
    const tabPanel = document.getElementById('prospect-details-tab-panel');
    if (header && tabPanel) {
      const headerHeight = header.offsetHeight;
      tabPanel.style.height = `calc(100vh - ${headerHeight}px)`;
    }
  };

  onCleanup(() => {
    window.removeEventListener('resize', updateTabPanelHeight);
  });

  const ProspectDetailsHeaderLoadingSkeleton: Component = () => {
    return (
      <Grid
        container
        backgroundColor="white"
        class="shadow-lg! h-full px-4 py-2 z-10 md:h-fit!"
      >
        <Grid container item size={{ xs: 12, md: 6 }}>
          <Box class="mr-4 flex items-center">
            <Skeleton variant="text" width={200} height={36} />
          </Box>
        </Grid>
        <Grid
          container
          item
          size={{ xs: 12, md: 6 }}
          gap={2}
          justifyContent="flex-end"
        >
          <Skeleton
            variant="rectangular"
            width={150}
            height={36}
            class="mx-2"
          />
          <Skeleton variant="rectangular" width={100} height={36} />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <SiteTitle title={'TMS'} />
      <Box
        class="flex justify-between items-center bg-white sticky shadow-lg top-0 z-10"
        id="prospect-details-header"
      >
        <Show
          when={!prospectDetailsStore.loading}
          fallback={<ProspectDetailsHeaderLoadingSkeleton />}
        >
          <Grid
            container
            backgroundColor="white"
            class="shadow-lg! h-full px-4 py-2 z-10 md:h-fit!"
          >
            <Show when={saveLoading()}>
              <AtgLoader />
            </Show>
            <Grid container item size={{ xs: 12, md: 6 }}>
              <Box class="mr-4 flex items-center">
                <Typography variant="h5">
                  {prospectDetailsStore.prospectDetails.name}
                </Typography>
              </Box>
            </Grid>
            <Grid
              container
              item
              size={{ xs: 12, md: 6 }}
              justifyContent="flex-end"
            >
              <Box class="flex gap-2 flex-wrap! items-center">
                <Button
                  variant="contained"
                  label="Submit for Credit"
                  class="mt-px!"
                  onClick={handleSubmitForCredit}
                  dataTestId={formatDataTestId('Submit for Credit', 'button')}
                />
                <Button
                  isLoading={saveLoading()}
                  disabled={saveLoading()}
                  startIcon={<ContentSaveIcon size={20} />}
                  variant="contained"
                  label="SAVE"
                  type="submit"
                  onClick={handleProspectDetailsSave}
                  class="mt-px! "
                  dataTestId={formatDataTestId('SAVE', 'button')}
                />
              </Box>
            </Grid>
          </Grid>
        </Show>
      </Box>
      <Stack
        class="bg-[#E1ECF2] overflow-y-auto"
        id="prospect-details-tab-panel"
      >
        <Box class="m-4  bg-white">
          <Stack spacing={2} p={2}>
            <Show
              when={!prospectDetailsStore.loading}
              fallback={
                <Skeleton variant="rectangular" width={'100%'} height={50} />
              }
            >
              <Box
                display="flex"
                alignItems="center"
                class="divide-x divide-[#353434]"
                border="1px solid #353434"
              >
                <For each={statuses}>
                  {(status) => (
                    <Box
                      flex={1}
                      class={`p-2 text-center bg-[#96c1d5] cursor-pointer
                 ${
                   status.name === 'Submitted'
                     ? 'pointer-events-none cursor-not-allowed'
                     : ''
                 } ${
                   prospectDetailsStore.prospectDetails.status === status.value
                     ? status.classes
                     : ''
                 }`}
                      onClick={() =>
                        updateProspectDetails({ status: status.value })
                      }
                    >
                      {status.name}
                    </Box>
                  )}
                </For>
              </Box>
            </Show>

            <Box>
              <TabsComponent
                tabs={tabs.map((item) => (
                  <Tab>{item}</Tab>
                ))}
                activeTab={activeTab()}
                border={true}
                tabStyleClasses="rounded-t-lg"
                activeStyling="bg-[#ffe8b7]"
                onTabClick={(index) => {
                  setActiveTab(index);
                }}
              />
              <div class="border-t border-gray-300 mb-2" />
            </Box>
            <Switch>
              <Match when={activeTab() === 0}>
                <ProspectDetailsInformation />
              </Match>
              <Match when={activeTab() === 1}>
                <Quotes type="Prospect" />
              </Match>
            </Switch>
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default ProspectDetails;
